import { AfterViewInit, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { DropDownListComponent } from '@progress/kendo-angular-dropdowns';
import _ from 'lodash';
import moment from 'moment';
import { combineLatest, Observable, Subject } from 'rxjs';
import { map, skipWhile, takeUntil, tap } from 'rxjs/operators';
import 'src/app/samwebDescription/samweb/extension-methods/array-extensions';
import { AgentDetailsBehaviourState } from 'src/app/store/dashboard/brick-wall/brick-behaviour-state/brickTypes/agent-details-behavior/agent-details-behavior.state';
import { SamwinAgentListState } from 'src/app/store/samwin/samwin-agent-list/samwin-agent-list.state';
import { RequestUserStatusChange } from 'src/app/store/samwin/samwin-user-status/samwin-user-status.actions';
import { SamwinUserStatusSelectors } from 'src/app/store/samwin/samwin-user-status/samwin-user-status.selectors';
import { SamwinUserStatusState } from 'src/app/store/samwin/samwin-user-status/samwin-user-status.state';
import { SupervisingRequestUserStatusChange } from 'src/app/store/samwin/supervising.actions';
import { SessionState } from 'src/app/store/session/session.state';
import { UserStatusInfoRemoteDto } from 'src/generated/signalR/user-status-info-remote-dto';
import { Originator } from 'src/generated/swagger';

@Component({
   selector: 'samweb-user-status-dropdown',
   templateUrl: './user-status-dropdown.component.html',
   styleUrls: ['./user-status-dropdown.component.scss'],
})
export class UserStatusDropdownComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
   @Select(SamwinUserStatusState.getNextAutoStatusChangeTime)
   public autoStatusChangeTime$: Observable<moment.Moment>;

   public widthForUserStatusChange$: Observable<string>;
   public percentUntilAutoChange$: Observable<number>;
   public possibleUserStates$: Observable<UserStatusInfoRemoteDto[]>;
   public currentUserStatusInfo$: Observable<UserStatusInfoRemoteDto>;
   public followUpStatusInfos$: Observable<[UserStatusInfoRemoteDto, UserStatusInfoRemoteDto[]]>;

   @Input()
   public userId: number = null;

   @Input()
   public disabled = false;

   @ViewChild('dropdownList') public dropdownList: DropDownListComponent;
   public showDropDown: boolean;

   @Input()
   public isViewSmall = true;

   private destroy$ = new Subject();

   constructor(private store: Store) {
      // nothing to do
   }

   public toggleDropdown() {
      this.dropdownList.toggle();
      this.updateShowDropDown();
   }

   private forLoggedOnUser() {
      return _.isNil(this.userId) || this.userId === this.store.selectSnapshot(SessionState.getAuthenticatedUser).userId;
   }

   ngOnChanges(changes: SimpleChanges) {
      if (changes.userId.previousValue !== changes.userId.currentValue) {
         this.ngOnInit();
      }
   }

   ngOnInit() {
      // use the currently logged on user if no special user is provided
      if (this.forLoggedOnUser()) {
         this.currentUserStatusInfo$ = this.store.select(SamwinUserStatusState.currentStatusInfo).pipe(skipWhile((value) => _(value).isNil()));

         this.followUpStatusInfos$ = combineLatest([this.currentUserStatusInfo$, this.store.select(SamwinUserStatusSelectors.getAllFollowUpStatusInfos)]);
      } else {
         this.currentUserStatusInfo$ = this.store
            .select(SamwinAgentListState.getCurrentStatusInfoFn)
            .pipe(map((getCurrentStatusInfoFn) => getCurrentStatusInfoFn(this.userId)));

         this.followUpStatusInfos$ = combineLatest([
            this.currentUserStatusInfo$,
            this.store.select(AgentDetailsBehaviourState.getAllFollowUpStatusInfosForSelectedUser),
         ]);
      }

      this.possibleUserStates$ = this.followUpStatusInfos$.pipe(
         map(([currentStatus, states]) => {
            if (_.isNil(currentStatus)) {
               return [];
            }
            const possibleStates = [...states.filter((ps) => ps.isSelectable)];
            // current status has to be part of the possible states
            // (otherwise the dropdown will not show the current status)
            if (!_(possibleStates).some((status) => status.id === currentStatus.id)) {
               possibleStates.push(currentStatus);
            }
            return possibleStates;
         }),
         map((statusInfos) => statusInfos.sortUserStatusInfoRemoteDtos())
      );
   }

   ngAfterViewInit(): void {
      this.updateShowDropDown();

      this.dropdownList.closed
         .pipe(
            tap(() => this.updateShowDropDown()),
            takeUntil(this.destroy$)
         )
         .subscribe();
   }

   ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
   }

   requestUserStatusChange(newValue: UserStatusInfoRemoteDto) {
      if (this.forLoggedOnUser()) {
         this.store.dispatch(new RequestUserStatusChange(newValue.id, Originator.User));
      } else {
         this.store.dispatch(new SupervisingRequestUserStatusChange(this.userId, newValue.id));
      }
   }

   private updateShowDropDown(): void {
      this.showDropDown = this.dropdownList.isOpen || this.isViewSmall;
   }
}
